import {AfterViewInit, ChangeDetectorRef, Component, HostListener, Inject, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Customer, InvoiceGet, LegalPerson, LegalPersonList, PurchaseTransaction} from '../../../../generated/sales-terminal-client';
import {CustomerService} from '../../../../services/customer/customer.service';
import {ScrollLoad} from '../../../../components/scroll-load-component/ScrollLoad';
import {AuthService} from '../../../../services/auth/auth.service';
import {TranslateService} from '@ngx-translate/core';
import {LegalPersonService} from '../../../../services/legalPerson/legalPerson.service';
import moment from 'moment';
import {ConfirmDialogComponent} from '../../../../components/confirm-dialog/confirm-dialog.component';
import {InvoiceService} from '../../../../services/invoice/invoice.service';
import {ViewService} from '../../../../services/view/view.service';
import {Privileges} from '../../../admin/privileges/privileges.constants';
import {finalize} from 'rxjs/operators';
import {Subscription, tap} from 'rxjs';
import {cloneDeep, isEqual} from 'lodash-es';
import {CONTINUE_WITHOUT_SAVING} from '../../sales-terminal.constants';
import {PendingChangesDialogComponent} from '../../../../components/pending-changes-dialog/pending-changes-dialog.component';
import {UtilService} from '../../../../services/util/util.service';
import {DataTable, DataTableRowItemType} from "../../../../components/data-table/data-table.interfaces";
import {TextInputType} from '../../../../components/forms/text-input/text-input.component';

interface DialogData {
  transaction: PurchaseTransaction;
  legacy: boolean;
}
export interface InvoiceData {
  customerUuid?: string;
  dueDate?: string;
  description?: string;
  comment?: string;
  legalPersonId?: number;
}

@Component({
  selector: 'app-invoice-generate-modal',
  templateUrl: './invoice-generate-modal.component.html',
  styleUrls: ['./invoice-generate-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class InvoiceGenerateModalComponent implements OnInit, OnDestroy, AfterViewInit {
  @HostListener('window:popstate', ['$event'])
  onPopState() {
    this.dialog.closeAll();
  }

  @ViewChild('tableEvent')
  tableEvent: TemplateRef<any>;
  @ViewChild('tableDate')
  tableDate: TemplateRef<any>;
  @ViewChild('tableTicketType')
  tableTicketType: TemplateRef<any>;
  @ViewChild('tableTicketPrice')
  tableTicketPrice: TemplateRef<any>;

  transaction: PurchaseTransaction;
  invoice: InvoiceGet = {};
  privateInvoice = false;
  createFormVisible = false;
  invoiceData: InvoiceData = {};
  invoiceDataOrig: InvoiceData = {};
  customer: Customer = {};
  legalPerson: LegalPerson;
  isLoading = false;
  isReady: boolean;
  isLegacy = false;
  currentDate = moment(new Date()).format();
  public tableConfig: DataTable;
  private _subs: Subscription[] = [];

  public customerScrollLoad: ScrollLoad<Customer> = this.customerService.getScrollLoad(20);
  public legalPersonScrollLoad: ScrollLoad<LegalPersonList> = this.legalPersonService.getScrollLoad(20);

  showDetails = true;

  private hasInvoiceGenerationPrivilege = this.authService.checkPrivileges(Privileges.INVOICE_GENERATE);
  private hasInvoiceChangePrivilege = this.authService.checkPrivileges(Privileges.INVOICE_CHANGE);
  private hasUserManagePrivilege = this.authService.hasUserManagePrivilege;
  private hasLegalChangePrivilege = this.authService.hasLegalChangePrivilege;
  private hasCustomerViewPrivilege = this.authService.checkPrivileges(Privileges.CUSTOMER_MANAGE);
  private hasLegalGetPrivilege = this.authService.hasLegalGetPrivilege;

  constructor(
    public dialogRef: MatDialogRef<InvoiceGenerateModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public customerService: CustomerService,
    public authService: AuthService,
    private translateService: TranslateService,
    public legalPersonService: LegalPersonService,
    private dialog: MatDialog,
    public invoiceService: InvoiceService,
    public viewService: ViewService,
    public utilService: UtilService,
    private cdr: ChangeDetectorRef,
  ) {
    this.transaction = this.data.transaction;
    this.isLegacy = this.data.legacy;
  }

  public ngOnInit(): void {
    this.isLoading = true;
    if (this.transaction?.invoiceUuid) {
      this.invoiceService.getInvoice(this.transaction.invoiceUuid).pipe(
        tap({error: () => this.isLoading = false})
      ).subscribe((invoice) => {
        this.invoice = invoice;
        this.mapInvoiceData();
      });
    } else {
      this.mapInvoiceData();
    }
    this.dialogRef.disableClose = true;
    this._subs.push(this.dialogRef.backdropClick().subscribe(() => {
      this.confirmClose();
    }));
  }

  public ngAfterViewInit(): void {
    this.tableConfig = {
      header: {
        items: [
          {
            key: 'ticket_id',
            format: 'general.api_cart_table_ticket_id'
          },
          {
            key: 'event',
            format: 'general.api_cart_table_event_title'
          },
          {
            key: 'date',
            format: 'general.api_cart_table_date'
          },
          {
            key: 'ticketType',
            format: 'general.api_cart_table_type'
          },
          {
            key: 'sector',
            format: 'general.api_cart_table_sector',
            isHidden: !this.isLegacy
          },
          {
            key: 'row',
            format: 'general.api_cart_table_row',
            isHidden: !this.isLegacy
          },
          {
            key: 'seat',
            format: 'general.api_cart_table_seat',
            isHidden: !this.isLegacy
          },
          {
            key: 'ticketPrice',
            format: 'general.api_cart_table_price',
            cssClass: 'align-right'
          }
        ]
      },
      body: {
        items: [
          {
            key: 'ticket_id',
            format: 'id',
            useModel: true,
            cssClass: 'title'
          },
          {
            key: 'event',
            useParent: true,
            type: DataTableRowItemType.Template,
            template: this.tableEvent
          },
          {
            key: 'date',
            useParent: true,
            type: DataTableRowItemType.Template,
            template: this.tableDate,
            cssClass: 'white-space-no-wrap'
          },
          {
            key: 'sector',
            format: 'sectorName',
            useModel: true,
            isHidden: !this.isLegacy
          },
          {
            key: 'row',
            format: 'row',
            useModel: true,
            isHidden: !this.isLegacy
          },
          {
            key: 'place',
            format: 'place',
            useModel: true,
            isHidden: !this.isLegacy
          },
          {
            key: 'ticketType',
            type: DataTableRowItemType.Template,
            template: this.tableTicketType
          },
          {
            key: 'ticketPrice',
            type: DataTableRowItemType.Template,
            template: this.tableTicketPrice,
            cssClass: 'align-right'
          }
        ]
      },
      footer: {
        items: []
      },
      customSelector: this.isLegacy ? 'legacyTickets' : 'tickets'
    }
    this.isReady = true;
    this.cdr.detectChanges();
  }

  public ngOnDestroy(): void {
    this._subs.forEach(s => s.unsubscribe());
    this._subs = [];
  }

  syncModels(): void {
    if (this.invoiceData?.dueDate) {
      this.invoiceData.dueDate = moment(this.invoiceData.dueDate).toISOString();
    }
    this.invoiceDataOrig = cloneDeep(this.invoiceData);
  }

  private mapInvoiceData(): void {
    if (this.invoice?.uuid) {
      this.invoiceData = {
        customerUuid: this.invoice.customerUuid,
        dueDate: this.invoice.dueDate,
        description: this.invoice.description,
        comment: this.invoice.comment,
        legalPersonId: this.invoice.legalPersonId
      };
      this.syncModels();
      this.privateInvoice = true;
      if (this.invoice.legalPersonId) {
        this.privateInvoice = false;
        this.legalPersonService.get(this.invoice.legalPersonId).pipe(
          finalize(() => this.isLoading = false)
        ).subscribe((legalPerson) =>
          this.legalPersonScrollLoad.object = legalPerson);
      } else if (this.invoice.customerUuid) {
        this.customerService.get(this.invoice.customerUuid).pipe(
          finalize(() => this.isLoading = false)
        ).subscribe((customer) =>
          this.customerScrollLoad.object = customer);
      } else {
        this.isLoading = false;
      }
    } else {
      this.invoiceData = {
        customerUuid: null,
        dueDate: this.currentDate,
        description: null,
        comment: null,
        legalPersonId: null
      };
      this.syncModels();
      this.isLoading = false;
    }
  }

  get isValid() {
    if (!this.invoiceData) {
      return false;
    }
    let customerId;
    if (this.privateInvoice) {
      customerId = true;
      this.clearLegalPerson();
    } else {
      customerId = this.invoiceData.legalPersonId;
      this.clearCustomer();
    }
    return customerId && this.isDueDateValid;
  }

  get hideInvalidDueDateHint() {
    if (!this.invoiceData.dueDate) {
      return true;
    }
    return this.isDueDateValid;
  }

  get isDueDateValid() {
    if (!this.invoiceData.dueDate) {
      return false;
    }
    let invoiceDueDate = moment(this.invoiceData.dueDate).toDate();
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    return invoiceDueDate && (currentDate <= invoiceDueDate);
  }

  clearCustomer() {
    this.invoiceData.customerUuid = null;
    this.customerScrollLoad.reset();
  }

  clearLegalPerson() {
    this.invoiceData.legalPersonId = null;
    this.legalPersonScrollLoad.reset();
  }

  public createInvoice(): void {
    if (this.isValid) {
      if (this.privateInvoice && !this.invoiceData.customerUuid) {
        const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
          width: '60%',
          panelClass: 'xs-popup',
          data: {
            title: this.translateService.instant('general.api_invoice_private_confirmation_title'),
            description: this.translateService.instant('general.api_invoice_private_confirmation_description'),
            buttonCancel: this.translateService.instant('general.api_invoice_private_confirmation_button_cancel'),
            buttonOk: this.translateService.instant('general.api_invoice_private_confirmation_button_ok')
          }
        });
        confirmDialogRef.afterClosed().subscribe((res) => {
          if (res) {
            this.close(this.invoiceData);
          }
        });
      } else {
        this.close(this.invoiceData);
      }
    }
  }

  openCreateForm() {
    if (!this.isReadOnly && this.canCreate) {
      this.clearCustomer();
      this.clearLegalPerson();
      this.createFormVisible = true;
    }
  }

  openEditForm() {
    this.createFormVisible = true;
  }

  closeCreateForm() {
    this.createFormVisible = false;
  }

  clearPreviousFields() {
    this.clearCustomer();
    this.clearLegalPerson();
    this.closeCreateForm();
  }

  selectCustomer(customer: Customer): void {
    this.invoiceData.customerUuid = customer.uuid;
    this.customerScrollLoad.object = customer;
    this.closeCreateForm();
  }

  selectLegalPerson(legalPerson: LegalPerson): void {
    this.invoiceData.legalPersonId = legalPerson.id;
    this.legalPersonScrollLoad.object = legalPerson as LegalPersonList;
    this.closeCreateForm();
  }

  get isReadOnly(): boolean {
    return (!this.invoice.uuid && !this.hasInvoiceGenerationPrivilege)
      || (this.invoice.uuid && !this.hasInvoiceChangePrivilege);
  }

  get canCreate(): boolean {
    return this.canChange;
  }

  get canChange(): boolean {
    return (this.privateInvoice && this.hasUserManagePrivilege) ||
      (!this.privateInvoice && this.hasLegalChangePrivilege);
  }

  get canGet(): boolean {
    return (this.privateInvoice && this.hasCustomerViewPrivilege) ||
      (!this.privateInvoice && this.hasLegalGetPrivilege);
  }

  get isBuyerDataReadOnly(): boolean {
    return this.isReadOnly || (this.createFormVisible && (!this.canCreate && !this.canChange));
  }

  get isModelChanged(): boolean {
    return !isEqual(this.invoiceData, this.invoiceDataOrig)
      || (!this.invoice?.uuid && (this.privateInvoice || !!this.invoice?.legalPersonId));
  }

  public confirmClose(): void {
    if (this.isModelChanged && !this.isReadOnly) {
      const pendingChanges = new PendingChangesDialogComponent(this.dialog, this.translateService, {
        pendingChangesDescription: this.translateService.instant('general.api_transactions_invoice_description_unsaved_changes'),
        buttonOk: this.translateService.instant('general.api_invoice_create'),
        canNotSave: !this.isValid
      });
      pendingChanges.dialogRef.afterClosed().subscribe((res) => {
        if (res === true) {
          this.close(this.invoiceData);
        } else if (res === CONTINUE_WITHOUT_SAVING) {
          this.close();
        }
      });
    } else {
      this.close();
    }
  }

  private close(data?: any): void {
    this.dialogRef.disableClose = false;
    this.dialogRef.close(data);
  }

  protected readonly TextInputType = TextInputType;
}
